import { Suspense, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Menu as MenuIcon,
  Bell as BellIcon,
  User as UserIcon,
  ChevronDown as ChevronDownIcon,
  Settings as SettingsIcon,
  LogOut as LogOutIcon,
  Factory as FactoryIcon,
  Home as HomeIcon,
  Cpu as CpuIcon,
  LayoutGrid as LayoutGridIcon,
  MapPin as MapPinIcon,
  AlertTriangle as AlertTriangleIcon,
  AlertCircle as AlertCircleIcon,
  BarChart3 as BarChart3Icon,
  Book as BookIcon,
  Play as PlayIcon,
  Plug as PlugIcon,
  Phone as PhoneIcon,
  Sun as SunIcon,
  Moon as MoonIcon,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useAuthStore,
  selectUserName,
  selectUserEmail,
} from "@/stores/authStore";
import {
  useSiteConfigStore,
  selectProductName,
  selectLogoUrl,
  selectApplicationIconUrl,
} from "@/stores/siteConfigStore";
import { useTheme } from "@/hooks/useTheme";
import { useSessionMonitoring } from "@/hooks/useSessionValidation";
import { useSidebarStore } from "@/stores/sidebarStore";
import { socketService } from "@/services/socketService";
import LanguageSelector from "@/components/LanguageSelector";
import FallbackNotification from "@/components/FallbackNotification";
import styles from "./MainLayout.module.css";

interface NavItem {
  path: string;
  labelKey: string;
  labelFallback: string;
  icon: LucideIcon;
  end?: boolean;
}

// i18n-exempt — `labelFallback` só entra se o catálogo perder a chave;
// o texto exibido vem de `labelKey`
const NAV_ITEMS: NavItem[] = [
  { path: "/", labelKey: "nav.dashboard", labelFallback: "Dashboard", icon: HomeIcon, end: true },
  { path: "/machines", labelKey: "nav.machines", labelFallback: "Máquinas", icon: CpuIcon },
  { path: "/public-panels", labelKey: "nav.publicPanels", labelFallback: "Painéis Públicos", icon: LayoutGridIcon },
  { path: "/plants", labelKey: "nav.plants", labelFallback: "Plantas", icon: MapPinIcon },
  { path: "/alarms", labelKey: "nav.alarms", labelFallback: "Alarmes", icon: AlertTriangleIcon },
  { path: "/alerts", labelKey: "nav.alerts", labelFallback: "Alertas", icon: AlertCircleIcon },
  { path: "/analytics", labelKey: "nav.analytics", labelFallback: "Analytics", icon: BarChart3Icon },
  { path: "/support/documentation", labelKey: "nav.documentation", labelFallback: "Documentação", icon: BookIcon },
  { path: "/support/tutorials", labelKey: "nav.tutorials", labelFallback: "Tutoriais", icon: PlayIcon },
  { path: "/integrations", labelKey: "nav.integrations", labelFallback: "Integrações", icon: PlugIcon },
  { path: "/support", labelKey: "nav.contacts", labelFallback: "Contatos", icon: PhoneIcon, end: true },
];

export default function MainLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  useSessionMonitoring();

  // Initialize Socket.IO once for the whole authenticated session so the
  // realtime status updates reach the Dashboard / Machines list (not just
  // MachineDetails). Mirrors the Vue behaviour where the socket connected
  // right after login.
  useEffect(() => {
    if (!socketService.isConnected) {
      socketService.initialize()
        .catch((err) =>
          console.error("[MainLayout] ❌ socket initialize failed:", err),
        );
    }
  }, []);

  // Pre-warm the most navigated chunks (Machines list + MachineDetails) right
  // after login so going back/forward doesn't flash the Suspense fallback.
  // Browsers cache the chunks; calls after the first import are no-ops.
  useEffect(() => {
    const idle = (cb: () => void) =>
      (window as any).requestIdleCallback?.(cb) ?? window.setTimeout(cb, 800);
    idle(() => {
      void import("@/views/Machines");
      void import("@/views/MachineDetails");
    });
  }, []);

  const userName = useAuthStore(selectUserName);
  const userEmail = useAuthStore(selectUserEmail);
  const logout = useAuthStore((s) => s.logout);

  const productName = useSiteConfigStore(selectProductName);
  const logoUrl = useSiteConfigStore(selectLogoUrl);
  const applicationIconUrl = useSiteConfigStore(selectApplicationIconUrl);
  const hasLogo = !!logoUrl;
  const hasApplicationIcon = !!applicationIconUrl;

  const { isCollapsed: sidebarCollapsed, isOpen: sidebarOpen, toggle: toggleCollapsed, toggleMobile, closeMobile } = useSidebarStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [unreadNotifications] = useState(0);

  // Keep browser favicon in sync with the tenant's application icon.
  useEffect(() => {
    if (!applicationIconUrl) return;
    const update = (selector: string, rel: string) => {
      let el = document.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        document.head.appendChild(el);
      }
      el.href = applicationIconUrl;
    };
    update('link[rel="icon"]', "icon");
    update('link[rel="apple-touch-icon"]', "apple-touch-icon");
  }, [applicationIconUrl]);

  // Close user menu on outside click.
  useEffect(() => {
    if (!showUserMenu) return;
    function onMouseDown(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showUserMenu]);

  // Sync the CSS variable with the initial sidebar state on mount.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-current-width",
      sidebarCollapsed ? "80px" : "260px",
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After the sidebar CSS transition finishes (200ms), fire window.resize so
  // ECharts instances and other size-aware components recalculate their layout.
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 210);
    return () => clearTimeout(timer);
  }, [sidebarCollapsed]);

  // Close mobile sidebar on route change.
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  function toggleSidebar() {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      toggleMobile();
    } else {
      toggleCollapsed();
    }
  }

  async function handleLogout() {
    await logout();
    navigate("/auth/login", { replace: true });
  }

  const sidebarClass = [
    styles.sidebar,
    sidebarOpen ? styles.sidebarOpen : "",
    sidebarCollapsed ? styles.sidebarCollapsed : "",
  ]
    .filter(Boolean)
    .join(" ");

  const layoutContentClass = [
    styles.layoutContent,
    sidebarCollapsed ? styles.layoutCollapsed : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.mainLayout}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <button
              type="button"
              className={styles.sidebarToggle}
              onClick={toggleSidebar}
              aria-label={
                sidebarCollapsed
                  ? t("header.expandMenu")
                  : t("header.collapseMenu")
              }
            >
              <MenuIcon size={20} />
            </button>

            <Link to="/" className={styles.logo}>
              {hasApplicationIcon ? (
                <img
                  src={applicationIconUrl!}
                  alt={productName}
                  className={styles.logoImage}
                />
              ) : hasLogo ? (
                <img
                  src={logoUrl!}
                  alt={productName}
                  className={styles.logoImage}
                />
              ) : (
                <div className={styles.logoIcon}>
                  <FactoryIcon size={28} />
                </div>
              )}
              <span className={styles.logoText}>{productName}</span>
            </Link>
          </div>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.headerAction}
              title={t("header.notifications")}
            >
              <BellIcon size={20} />
              {unreadNotifications > 0 ? (
                <span className={styles.notificationBadge}>
                  {unreadNotifications}
                </span>
              ) : null}
            </button>

            <button
              type="button"
              className={styles.headerAction}
              onClick={toggleTheme}
              title={t("auth.theme.toggle")}
            >
              {isDark ? <SunIcon size={20} /> : <MoonIcon size={20} />}
            </button>

            <LanguageSelector />

            <div className={styles.userMenu} ref={userMenuRef}>
              <button
                type="button"
                className={styles.userButton}
                onClick={() => setShowUserMenu((v) => !v)}
              >
                <div className={styles.userAvatar}>
                  <UserIcon size={18} />
                </div>
                <span className={styles.userName}>{userName}</span>
                <ChevronDownIcon
                  size={16}
                  className={`${styles.chevron}${showUserMenu ? ` ${styles.chevronOpen}` : ""}`}
                />
              </button>

              {showUserMenu ? (
                <div className={styles.userDropdown}>
                  <div className={styles.userInfo}>
                    <div className={styles.userDetailsName}>{userName}</div>
                    <div className={styles.userDetailsEmail}>{userEmail}</div>
                  </div>
                  <div className={styles.dropdownDivider} />
                  <Link
                    to="/profile"
                    className={styles.dropdownItem}
                    onClick={() => setShowUserMenu(false)}
                  >
                    <UserIcon size={16} />
                    {t("header.profile")}
                  </Link>
                  <Link
                    to="/settings"
                    className={styles.dropdownItem}
                    onClick={() => setShowUserMenu(false)}
                  >
                    <SettingsIcon size={16} />
                    {t("header.settings")}
                  </Link>
                  <div className={styles.dropdownDivider} />
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                  >
                    <LogOutIcon size={16} />
                    {t("header.logout")}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {/* Layout content */}
      <div className={layoutContentClass}>
        <aside className={sidebarClass}>
          <div
            className={`${styles.sidebarOverlay}${sidebarOpen ? ` ${styles.sidebarOverlayVisible}` : ""}`}
            onClick={closeMobile}
          />
          <div className={styles.sidebarContent}>
            <nav className={styles.navigation}>
              {NAV_ITEMS.map(({ path, labelKey, labelFallback, icon: Icon, end }) => (
                <NavLink
                  key={path}
                  to={path}
                  end={end}
                  className={({ isActive }) =>
                    `${styles.navItem}${isActive ? ` ${styles.navItemActive}` : ""}`
                  }
                  onClick={closeMobile}
                  title={sidebarCollapsed ? t(labelKey, labelFallback) : ""}
                >
                  <Icon size={20} />
                  <span
                    className={`${styles.navText}${sidebarCollapsed ? ` ${styles.navTextHidden}` : ""}`}
                  >
                    {t(labelKey, labelFallback)}
                  </span>
                </NavLink>
              ))}
            </nav>
          </div>
        </aside>

        <main className={styles.mainContent}>
          <Suspense
            fallback={
              <div style={{ opacity: 0.6, fontSize: "0.875rem", padding: "1rem" }}>
                {t("common.loading")}
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>

      <FallbackNotification />
    </div>
  );
}
